import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, formatNumber } from "@/lib/api";
import { Loader2, TrendingUp, TrendingDown, Minus, AlertCircle } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const MONTH_SHORT = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

const monthLabel = (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-").map(Number);
  return `${MONTH_SHORT[m - 1]} ${String(y).slice(-2)}`;
};

const pctDelta = (curr, prev) => {
  if (curr == null || prev == null) return null;
  if (prev === 0) return curr > 0 ? Infinity : 0;
  return ((curr - prev) / prev) * 100;
};

const DeltaBadge = ({ delta, invert = false }) => {
  if (delta == null) return <span className="text-slate-300 text-xs">–</span>;
  if (delta === Infinity)
    return <span className="text-emerald-600 text-xs font-semibold">nuevo</span>;
  if (Math.abs(delta) < 1) return <Minus className="w-3 h-3 text-slate-400" />;
  const isUp = delta > 0;
  const isGood = invert ? !isUp : isUp;
  const color = Math.abs(delta) >= 10
    ? (isGood ? "text-emerald-600" : "text-rose-600")
    : "text-amber-600";
  const Icon = isUp ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${color}`}>
      <Icon className="w-3 h-3" />
      {isUp ? "+" : ""}{delta.toFixed(0)}%
    </span>
  );
};

const Cell = ({ value, kind }) => {
  if (value == null) return <span className="text-slate-300">—</span>;
  if (kind === "stock" && value === 0) return <span className="text-slate-300">—</span>;
  if (kind === "money") return <span className="tabular-nums">${formatNumber(value)}</span>;
  if (kind === "percent") return <span className="tabular-nums">{value.toFixed(2).replace(".", ",")}%</span>;
  if (kind === "decimal") return <span className="tabular-nums">{Number(value).toFixed(1).replace(".", ",")}</span>;
  return <span className="tabular-nums">{formatNumber(value)}</span>;
};

/**
 * Monthly evolution table.
 * Default: communications metrics (GET /clients/{id}/communications/monthly-metrics).
 * Pass `endpoint` + `title`/`subtitle` to reuse for other areas (BD, etc).
 */
export default function MonthlyMetricsTable({
  clientId,
  endpoint,
  title = "Tablero mensual",
  subtitle = "Evolución mes a mes de todo lo que medimos. Solo muestra meses cerrados; el mes en curso aparece cuando termina.",
  sourceOptions = null,   // [{key,label}] enables the domain switcher
  defaultSource = "all",  // 'all' → combinado
}) {
  const [monthsBack, setMonthsBack] = useState(6);
  const [source, setSource] = useState(defaultSource);
  const ep = endpoint || `/clients/${clientId}/communications/monthly-metrics`;

  const { data, isLoading, error } = useQuery({
    queryKey: ["client-monthly-metrics", clientId, ep, monthsBack, source],
    queryFn: () =>
      api
        .get(ep, {
          params: {
            months: monthsBack,
            ...(sourceOptions && source !== "all" ? { source } : {}),
          },
        })
        .then((r) => r.data),
    enabled: !!clientId,
  });

  const sections = data?.sections || [];
  const months = data?.months || [];
  const baseline = data?.baseline || null;

  const totalRow = useMemo(() => {
    // Header colspan calc — 1 name + [baseline + its delta] + N months + N-1 deltas
    return 1 + (baseline ? 2 : 0) + months.length + Math.max(months.length - 1, 0);
  }, [months.length, baseline]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Cargando tablero mensual…
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-4 bg-rose-50 border border-rose-200 rounded-lg text-rose-700 text-sm">
        <AlertCircle className="w-4 h-4" />
        Error al cargar el tablero: {error.response?.data?.detail || error.message}
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="monthly-metrics-table">
      {/* Header + range selector */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
          <p className="text-sm text-slate-500">{subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          {sourceOptions && sourceOptions.length > 1 && (
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger className="w-[200px]" data-testid="monthly-source-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Combinado (todos)</SelectItem>
                {sourceOptions.map((s) => (
                  <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={String(monthsBack)} onValueChange={(v) => setMonthsBack(Number(v))}>
            <SelectTrigger className="w-[160px]" data-testid="monthly-range-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="3">Últimos 3 meses</SelectItem>
              <SelectItem value="6">Últimos 6 meses</SelectItem>
              <SelectItem value="9">Últimos 9 meses</SelectItem>
              <SelectItem value="12">Últimos 12 meses</SelectItem>
              <SelectItem value="18">Últimos 18 meses</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {data?.metricool_error || data?.skylead_error || data?.briefs_error ? (
        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
          <AlertCircle className="w-4 h-4" />
          Alguna fuente respondió con error ({data?.metricool_error || data?.skylead_error || data?.briefs_error}). Los datos disponibles se muestran igual, los faltantes aparecen como "—".
        </div>
      ) : null}

      {sections.length === 0 ? (
        <div className="p-8 text-center text-slate-500 bg-slate-50 rounded-lg border border-slate-200">
          Aún no hay datos suficientes. Necesitás mapeos de Metricool y publicaciones cargadas.
        </div>
      ) : (
        <div className="overflow-x-auto border border-slate-200 rounded-lg bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="sticky left-0 z-10 bg-slate-50 text-left px-4 py-2.5 font-semibold text-slate-700 min-w-[240px]">
                  Métrica
                </th>
                {baseline && (
                  <>
                    <th className="text-right px-3 py-2.5 font-semibold text-slate-700 whitespace-nowrap" data-testid="baseline-col-header">
                      Baseline
                      <span className="block text-[10px] font-normal text-slate-400">{baseline.label.replace("Baseline ", "")}</span>
                    </th>
                    <th className="text-center px-2 py-2.5 font-medium text-slate-400 text-xs whitespace-nowrap">Δ</th>
                  </>
                )}
                {months.map((m, idx) => (
                  <React.Fragment key={m}>
                    <th className="text-right px-3 py-2.5 font-semibold text-slate-700 whitespace-nowrap">
                      {monthLabel(m)}
                    </th>
                    {idx < months.length - 1 && (
                      <th className="text-center px-2 py-2.5 font-medium text-slate-400 text-xs whitespace-nowrap">
                        Δ
                      </th>
                    )}
                  </React.Fragment>
                ))}
              </tr>
            </thead>

            {sections.map((section) => (
              <tbody key={section.title}>
                <tr className="bg-slate-100/60 border-y border-slate-200">
                  <td
                    colSpan={totalRow}
                    className="sticky left-0 px-4 py-2 text-xs font-semibold text-slate-600 uppercase tracking-wide"
                  >
                    {section.title}
                  </td>
                </tr>
                {section.metrics.map((metric, mi) => (
                  <tr
                    key={metric.name}
                    className={mi % 2 === 0 ? "bg-white" : "bg-slate-50/40"}
                  >
                    <td className="sticky left-0 z-10 bg-inherit px-4 py-2 text-slate-800 font-medium">
                      {metric.name}
                    </td>
                    {baseline && (
                      <>
                        <td className="text-right px-3 py-2 text-slate-500 bg-slate-50/60">
                          <Cell value={metric.baseline} kind={metric.kind} />
                        </td>
                        <td className="text-center px-2 py-2 bg-slate-50/60">
                          <DeltaBadge delta={pctDelta(metric.values[0], metric.baseline)} invert={metric.invert} />
                        </td>
                      </>
                    )}
                    {metric.values.map((v, i) => (
                      <React.Fragment key={i}>
                        <td className="text-right px-3 py-2 text-slate-700">
                          <Cell value={v} kind={metric.kind} />
                        </td>
                        {i < metric.values.length - 1 && (
                          <td className="text-center px-2 py-2">
                            <DeltaBadge delta={pctDelta(metric.values[i + 1], v)} invert={metric.invert} />
                          </td>
                        )}
                      </React.Fragment>
                    ))}
                  </tr>
                ))}
              </tbody>
            ))}
          </table>
        </div>
      )}
    </div>
  );
}
